
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { mergeStyleSets } from 'office-ui-fabric-react/lib/Styling';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import get from 'lodash/get';

import { getVymeraStringWithUnitHa } from '../../common/common/iskn/vymera';
import Text from '../../common/client/components/Text';

import { getActualCategoryName } from '../../reducers/category';
import categoryGetActions from '../../sagas/category';
import exportDataGetActions from '../../sagas/exportData';
import DialogImportCategoryParcel from './DialogImportCategoryParcel';
import DialogSelectCategory from './DialogSelectCategory';
import ObjectInfoTitle from './ObjectInfoTitle';
import ObjectInfoValues from './ObjectInfoValues';
import ToolbarCommandButton from './ToolbarCommandButton';

const classNames = mergeStyleSets({
    root: {
        borderBottom: '1px solid #dddddd',
        paddingBottom: 8
    },
    itemTitle: {
        width: 150,
        paddingLeft: 16
    },
    itemCount: {
        width: 50,
        textAlign: 'right'
    },
    itemVymera: {
        width: 100,
        textAlign: 'right'
    },
    itemColor: {
        width: 42,
        paddingLeft: 8
    },
    summaryRow: {
        paddingTop: 8
    },
    legendColor: {
        width: 32,
        height: 16,
        border: '1px solid #777777'
    }
});

const CategoryItem = (props) => {
    const color = props.item.item_color
        ? `rgba(${props.item.item_color.split(' ').join(',')},0.7)`
        : null;

    return (
        <Stack horizontal>
            <Stack.Item className={classNames.itemTitle}>
                <Text ellipsis title={props.item.item_title}>{props.item.item_title}</Text>
            </Stack.Item>
            <Stack.Item className={classNames.itemCount}>
                <Text bold>{props.item.count}</Text>
            </Stack.Item>
            <Stack.Item className={classNames.itemVymera}>
                <Text bold>{getVymeraStringWithUnitHa(props.item.vymera)}</Text>
            </Stack.Item>
            {props.item.item_color &&
                <Stack.Item className={classNames.itemColor}>
                    <div className={classNames.legendColor} style={{backgroundColor: color}} />
                </Stack.Item>
            }
        </Stack>
    );
}

const Category = (props) => {
    let sumCount = 0;
    let sumVymera = 0;
    return (
        <Stack>
            {props.items.map(item => {
                let itemSummary = props.category.reduce((previous, current) => {
                    return (current.item_name === item.item_name)
                        ? current
                        : previous;
                }, {count: 0, vymera: 0});

                sumCount = sumCount + itemSummary.count;
                sumVymera = sumVymera + itemSummary.vymera;
                return (
                    <CategoryItem
                        key={item.item_name}
                        item={{
                            item_title: item.item_title,
                            item_color: item.item_color,
                            count: itemSummary.count,
                            vymera: itemSummary.vymera
                        }}
                    />
                );
            })}
            <div className={classNames.summaryRow}>
                <CategoryItem item={{item_title: 'Celkem', count: sumCount, vymera: sumVymera}} />
            </div>
        </Stack>
    );
}

const ObjectInfoCategoryParcel = (props) => {
    const dispatch = useDispatch();
    const categoryActions = categoryGetActions(dispatch);
    const exportDataActions = exportDataGetActions(dispatch);

    const token = useSelector(state => {
        return state.auth.data ? state.auth.data.user.token : null;
    });

    const projectId = useSelector(state => state.project.data.project.id);
    const category = useSelector(state => state.category);
    const actualCategoryName = useSelector(state => getActualCategoryName(state));
    const actualListName = useSelector(state => state.list.actualListName);

    const categories = get(category, 'data.result.category.categories', []);

    let actualCategory = categories.reduce((previous, current) => {
        return (current.name === category.actualCategoryFullName.split(':')[1])
            ? current
            : previous;
    }, null);

    let actualCategoryItems = category.actualCategoryFullName
        ? category.categoryItems[category.actualCategoryFullName.split(':')[1]] || []
        : [];

    let actualCategoryItemParcel = category.actualCategoryFullName
        ? category.categoryItemParcel[category.actualCategoryFullName] || []
        : [];

    useEffect(() => {
        if (!category.loaded && !category.loading) {
            categoryActions.categoryGet(token, projectId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleChangeClick = async () => {
        let data = await DialogSelectCategory.show(
            categories, category.actualCategoryFullName, actualListName
        );
        if (data) {
            categoryActions.setActualCategory(data.selectedCategory);
        }
    }

    const handleExportClick = () => {
        exportDataActions.exportParcCategoryJson(
            token, projectId, actualCategoryName
        );
    }

    const handleImportClick = () => {
        DialogImportCategoryParcel.show(
            actualCategoryItemParcel,
            actualCategoryItems,
            handleImportStart
        );
    }

    const handleImportStart = (categoryItems, parc, keepCategoryItemNames, importMode) => {
        categoryActions.importCategoryData(
            token, projectId, actualListName, actualCategoryName,
            categoryItems, parc, keepCategoryItemNames, importMode
        );
    }

    return (
        <div className={classNames.root}>
            <Stack>
                <Stack.Item>
                    <ObjectInfoTitle title="KATEGORIE PARCEL" />
                </Stack.Item>
                <Stack.Item>
                    {category.loaded
                        ? <Stack tokens={{childrenGap:8}}>
                            <ObjectInfoValues
                                items={[
                                    {
                                        title: 'Aktuální kategorie',
                                        value: actualCategory.title
                                    }
                                ]}
                            />
                            <Category
                                category={actualCategoryItemParcel}
                                items={actualCategoryItems}
                            />
                            <Stack.Item>
                                <ToolbarCommandButton items={[
                                    {
                                        text: 'Import',
                                        title: 'Import dat do aktuální kategorie parcel',
                                        onClick: handleImportClick
                                    },
                                    {
                                        text: 'Export',
                                        title: 'Export dat aktuální kategorie parcel',
                                        onClick: handleExportClick
                                    },
                                    {
                                        text: 'Změnit',
                                        title: 'Změnit akuální kategorii',
                                        onClick: handleChangeClick
                                    }
                                ]}/>
                            </Stack.Item>
                        </Stack>
                        : <Text>Načítám data...</Text>
                    }
                </Stack.Item>
            </Stack>
        </div>
    );
}

export default ObjectInfoCategoryParcel;
